import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../ui/Button';
import { Card, CardHeader, CardContent } from '../../ui/Card';
import type { BigBookQuestionResponse, ApologyResponse } from '../../../types/checkin';
import {
  ExclamationTriangleIcon,
  HeartIcon,
  EyeSlashIcon,
  ShieldExclamationIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

interface BigBookQuestionsProps {
  onComplete: (responses: {
    resentful: BigBookQuestionResponse;
    selfish: BigBookQuestionResponse;
    dishonest: BigBookQuestionResponse;
    afraid: BigBookQuestionResponse;
    oweApology: ApologyResponse;
  }) => void;
  onBack: () => void;
}

interface Question {
  id: 'resentful' | 'selfish' | 'dishonest' | 'afraid';
  text: string;
  icon: typeof ExclamationTriangleIcon;
  color: string;
}

const questions: Question[] = [
  {
    id: 'resentful',
    text: 'Was I resentful today?',
    icon: ExclamationTriangleIcon,
    color: 'text-danger-500',
  },
  {
    id: 'selfish',
    text: 'Was I selfish today?',
    icon: HeartIcon,
    color: 'text-warning-500',
  },
  {
    id: 'dishonest',
    text: 'Was I dishonest today?',
    icon: EyeSlashIcon,
    color: 'text-primary-500',
  },
  {
    id: 'afraid',
    text: 'Was I afraid today?',
    icon: ShieldExclamationIcon,
    color: 'text-accent-500',
  },
];

export function BigBookQuestions({ onComplete, onBack }: BigBookQuestionsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, BigBookQuestionResponse>>({});
  const [apologyResponse, setApologyResponse] = useState<ApologyResponse | null>(null);

  // Check if we're on the apology question (after all Big Book questions)
  const isApologyQuestion = currentIndex === questions.length;

  const currentQuestion = isApologyQuestion ? null : questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const currentResponse = currentQuestion ? responses[currentQuestion.id] : undefined;

  const handleYes = () => {
    if (!currentQuestion) return;
    setResponses({
      ...responses,
      [currentQuestion.id]: { answer: true },
    });
  };

  const handleNo = () => {
    if (!currentQuestion) return;
    setResponses({
      ...responses,
      [currentQuestion.id]: { answer: false },
    });
  };

  const handleDetails = (details: string) => {
    if (!currentQuestion) return;
    setResponses({
      ...responses,
      [currentQuestion.id]: { answer: true, details },
    });
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // After last Big Book question, ask about apology
      setCurrentIndex(questions.length);
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      onBack();
    }
  };

  const handleApologyYes = () => {
    setApologyResponse({ answer: true });
  };

  const handleApologyNo = () => {
    setApologyResponse({ answer: false });
  };

  const handleApologyDetails = (to: string, when: string) => {
    setApologyResponse({ answer: true, to, when });
  };

  const handleComplete = () => {
    onComplete({
      resentful: responses.resentful || { answer: false },
      selfish: responses.selfish || { answer: false },
      dishonest: responses.dishonest || { answer: false },
      afraid: responses.afraid || { answer: false },
      oweApology: apologyResponse || { answer: false },
    });
  };

  // Apology question screen
  if (currentIndex === questions.length) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Progress */}
          <div className="text-center text-sm text-surface-500 dark:text-surface-400">
            Final Question
          </div>

          {/* Question Card */}
          <Card variant="default">
            <CardHeader
              title="Do you owe anyone an apology?"
              action={<ChatBubbleLeftRightIcon className="w-6 h-6 text-primary-500" />}
            />
            <CardContent>
              <div className="space-y-4">
                {/* Yes/No Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={apologyResponse?.answer === true ? 'primary' : 'secondary'}
                    size="lg"
                    fullWidth
                    onClick={handleApologyYes}
                  >
                    Yes
                  </Button>
                  <Button
                    variant={apologyResponse?.answer === false ? 'primary' : 'secondary'}
                    size="lg"
                    fullWidth
                    onClick={handleApologyNo}
                  >
                    No
                  </Button>
                </div>

                {/* Details if Yes */}
                {apologyResponse?.answer === true && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-3 pt-2"
                  >
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Who do you owe an apology to?
                      </label>
                      <input
                        type="text"
                        placeholder="Name or description"
                        value={apologyResponse?.to || ''}
                        onChange={(e) =>
                          handleApologyDetails(e.target.value, apologyResponse?.when || '')
                        }
                        className="w-full px-4 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        When will you make this apology?
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Tomorrow morning, This weekend"
                        value={apologyResponse?.when || ''}
                        onChange={(e) =>
                          handleApologyDetails(apologyResponse?.to || '', e.target.value)
                        }
                        className="w-full px-4 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100"
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex gap-3">
            <Button variant="secondary" size="lg" onClick={handlePrevious} className="flex-1">
              Back
            </Button>
            <Button variant="primary" size="lg" onClick={handleComplete} className="flex-1">
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Regular Big Book question screen
  if (!currentQuestion) {
    return null; // Safety fallback
  }

  const Icon = currentQuestion.icon;

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Progress */}
        <div className="text-center text-sm text-surface-500 dark:text-surface-400">
          Question {currentIndex + 1} of {questions.length}
        </div>

        {/* Question Card */}
        <Card variant="default">
          <CardHeader
            title={currentQuestion.text}
            action={<Icon className={`w-6 h-6 ${currentQuestion.color}`} />}
          />
          <CardContent>
            <div className="space-y-4">
              {/* Yes/No Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={currentResponse?.answer === true ? 'primary' : 'secondary'}
                  size="lg"
                  fullWidth
                  onClick={handleYes}
                >
                  Yes
                </Button>
                <Button
                  variant={currentResponse?.answer === false ? 'primary' : 'secondary'}
                  size="lg"
                  fullWidth
                  onClick={handleNo}
                >
                  No
                </Button>
              </div>

              {/* Details if Yes */}
              {currentResponse?.answer === true && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="pt-2"
                >
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Tell us more (optional)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="What happened? How did it make you feel?"
                    value={currentResponse.details || ''}
                    onChange={(e) => handleDetails(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100 resize-none"
                  />
                </motion.div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-3">
          <Button variant="secondary" size="lg" onClick={handlePrevious} className="flex-1">
            Back
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleNext}
            disabled={currentResponse === undefined}
            className="flex-1"
          >
            {isLastQuestion ? 'Next' : 'Next Question'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default BigBookQuestions;
